import React, { useState } from "react";
import { Eye, Edit3, Trash, Link2, ChevronDown, ChevronUp } from "lucide-react"; // Importing icons from Lucide React
import SalesForm from "../components/SalesForm";
import SaleDetails from "../components/SaleDetails";
import LinkWorkOrderModal from "../components/LinkWorkOrderModal";

function SalesPage() {
  const [sales, setSales] = useState([
    {
      sale_id: "SALE001",
      customer_name: "John Doe",
      sale_date: "2025-01-10",
      total_amount: 1500,
      payment_status: "Paid",
      delivery_status: "Delivered",
      products: [{ product_name: "Laptop", quantity: 1, unit_price: 1500 }],
      work_orders: [
        { id: "WO001", name: "Curtains Installation", status: "In Progress" },
      ],
    },
    {
      sale_id: "SALE002",
      customer_name: "Jane Smith",
      sale_date: "2025-01-12",
      total_amount: 2000,
      payment_status: "Pending",
      delivery_status: "Scheduled",
      products: [
        { product_name: "Software License", quantity: 1, unit_price: 2000 },
      ],
      work_orders: [],
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSale, setSelectedSale] = useState(null);
  const [expandedSaleId, setExpandedSaleId] = useState(null); // Track which sale is expanded
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLinkWorkOrderOpen, setIsLinkWorkOrderOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState(""); // Filter by Payment Status
  const [deliveryFilter, setDeliveryFilter] = useState(""); // Filter by Delivery Status

  // Add a new sale
  const handleAddSale = (newSale) => {
    setSales([
      ...sales,
      {
        ...newSale,
        sale_id: `SALE${Date.now()}`, // Generate a unique sale ID
        sale_date: new Date().toISOString(),
        work_orders: [],
      },
    ]);
    setIsFormOpen(false);
  };

  // Edit an existing sale
  const handleEditSale = (updatedSale) => {
    setSales(
      sales.map((sale) =>
        sale.sale_id === updatedSale.sale_id ? updatedSale : sale
      )
    );
    setIsFormOpen(false);
  };

  // Delete a sale
  const handleDeleteSale = (sale_id) => {
    if (window.confirm("Are you sure you want to delete this sale?")) {
      setSales(sales.filter((sale) => sale.sale_id !== sale_id));
      setSelectedSale(null);
    }
  };

  // Toggle expand/collapse for sales
  const toggleExpand = (sale_id) => {
    setExpandedSaleId(expandedSaleId === sale_id ? null : sale_id);
  };

  // Filter sales based on the search query, payment status, and delivery status
  const filteredSales = sales.filter((sale) => {
    const matchesSearch = sale.customer_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesPaymentStatus =
      !paymentFilter || sale.payment_status === paymentFilter;
    const matchesDeliveryStatus =
      !deliveryFilter || sale.delivery_status === deliveryFilter;
    return matchesSearch && matchesPaymentStatus && matchesDeliveryStatus;
  });

  // Calculate the total sales amount
  const totalSales = sales.reduce((sum, sale) => sum + sale.total_amount, 0);

  // Open the Sales Details modal
  const handleViewDetails = (sale) => {
    setSelectedSale(sale); // Set the selected sale
    setIsDetailsOpen(true); // Open the Sales Details modal
  };

  // Open the Link Work Order Modal
  const handleOpenLinkWorkOrderModal = (sale) => {
    setSelectedSale(sale); // Set the selected sale
    setIsLinkWorkOrderOpen(true); // Open the Link Work Order modal
  };

  // Link work orders to a sale
  const handleLinkWorkOrders = (sale_id, linkedWorkOrders) => {
    setSales(
      sales.map((sale) =>
        sale.sale_id === sale_id
          ? { ...sale, work_orders: linkedWorkOrders }
          : sale
      )
    );
    setIsLinkWorkOrderOpen(false);
  };

  return (
    <div className="relative p-4 max-w-6xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
  <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-2 md:mb-0">
    Sales
  </h2>
  <button
    className="bg-blue-600 text-white px-3 py-2 text-sm rounded-md hover:bg-blue-500"
    onClick={() => setIsFormOpen(true)}
  >
     + Add Sale
  </button>
</div>

      {/* Search and Filters Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <input
          type="text"
          placeholder="Search Sales by Customer"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border p-2 rounded-md w-full md:w-1/2"
        />
        <select
          className="border p-2 rounded-md"
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
        >
          <option value="">Filter by Payment Status</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
        </select>
        <select
          className="border p-2 rounded-md"
          value={deliveryFilter}
          onChange={(e) => setDeliveryFilter(e.target.value)}
        >
          <option value="">Filter by Delivery Status</option>
          <option value="Delivered">Delivered</option>
          <option value="Scheduled">Scheduled</option>
        </select>
        {/*<div className="text-right">
          <p className="text-gray-600">
            Total Sales:{" "}
            <span className="text-gray-900 font-semibold">
              ${totalSales.toLocaleString()}
            </span>
          </p>
        </div>*/}
      </div>

      {/* Sales List Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-md pb-6">
      <table className="min-w-full table-auto text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-2">Sale ID</th>
              <th className="px-4 py-2">Customer</th>
              <th className="px-4 py-2">Total</th>
              <th className="px-4 py-2">Payment Status</th>
              <th className="px-4 py-2">Delivery Status</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSales.map((sale) => (
              <React.Fragment key={sale.sale_id}>
                <tr>
                  <td className="px-4 py-2">{sale.sale_id}</td>
                  <td className="px-4 py-2 truncate max-w-[150px]">{sale.customer_name}</td>
                  <td className="px-4 py-2 text-right">${sale.total_amount.toLocaleString()}</td>
                  <td className="px-4 py-2 text-center">{sale.payment_status}</td>
                  <td className="px-4 py-2 text-center">{sale.delivery_status}</td>
                  <td className="px-2 py-2 flex items-center justify-center space-x-1">
  <button onClick={() => handleViewDetails(sale)} className="text-blue-600 hover:text-blue-800 p-1">
    <Eye size={14} />
  </button>
  <button onClick={() => { setSelectedSale(sale); setIsFormOpen(true); }} className="text-green-600 hover:text-green-800 p-1">
    <Edit3 size={14} />
  </button>
  <button onClick={() => handleDeleteSale(sale.sale_id)} className="text-red-600 hover:text-red-800 p-1">
    <Trash size={14} />
  </button>
  <button onClick={() => handleOpenLinkWorkOrderModal(sale)} className="text-purple-600 hover:text-purple-800 p-1">
    <Link2 size={14} />
  </button>
  <button onClick={() => toggleExpand(sale.sale_id)} className="text-gray-600 hover:text-gray-800 p-1">
    {expandedSaleId === sale.sale_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
  </button>
</td>
                </tr>
                {expandedSaleId === sale.sale_id && (
                  <tr>
                    <td colSpan="6" className="px-4 py-2 bg-gray-50">
                      <div className="space-y-4">
                        {/* Products Section */}
                        <div>
                          <h4 className="font-semibold text-gray-800">
                            Products
                          </h4>
                          {sale.products.length > 0 ? (
                            <ul className="list-disc ml-5 text-gray-600">
                              {sale.products.map((product, idx) => (
                                <li key={idx}>
                                  {product.product_name} - Quantity:{" "}
                                  {product.quantity}, Unit Price: $
                                  {product.unit_price.toLocaleString()}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-600">No products added.</p>
                          )}
                        </div>

                        {/* Work Orders Section */}
                        <div>
                          <h4 className="font-semibold text-gray-800">
                            Work Orders
                          </h4>
                          {sale.work_orders.length > 0 ? (
                            <ul className="list-disc ml-5 text-gray-600">
                              {sale.work_orders.map((wo) => (
                                <li key={wo.id}>
                                  {wo.name} - Status: {wo.status}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-600">
                              No work orders linked.
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sale Details Modal */}
      {isDetailsOpen && selectedSale && (
        <SaleDetails
          sale={selectedSale}
          onClose={() => setIsDetailsOpen(false)} // Close the Sales Details modal
          onEdit={handleEditSale}
        />
      )}

      {/* Add/Edit Sale Form */}
      {isFormOpen && (
        <SalesForm
          sale={selectedSale}
          onClose={() => setIsFormOpen(false)}
          onSave={selectedSale ? handleEditSale : handleAddSale}
        />
      )}

      {/* Link Work Order Modal */}
      {isLinkWorkOrderOpen && selectedSale && (
        <LinkWorkOrderModal
          sale={selectedSale} // Pass the selected sale object to the modal
          onClose={() => setIsLinkWorkOrderOpen(false)} // Close the Link Work Order modal
          onSave={(linkedWorkOrders) =>
            handleLinkWorkOrders(selectedSale.sale_id, linkedWorkOrders)
          }
        />
      )}
    </div>
  );
}

export default SalesPage;